import { Box, styled, Theme, useTheme } from '@mui/material';
import React, { CSSProperties, ImgHTMLAttributes } from 'react';
import { CampaignType } from '../../../types';
import { getImageAssetUrl } from '../../../utils/assetUtils';

import { formatMoney } from '../../../utils/stringUtils';

type RewardPillWithTextProps = {
  rewardAmount: number;
  rewardCampaignType: CampaignType;
  rewardPercent?: number;
  rewardPercentText?: string;
  rewardPercentTextOverride?: string;
  containerStyle?: CSSProperties;
  pillContainerStyle?: CSSProperties;
  onPillClick?: React.MouseEventHandler<HTMLDivElement>;
  baseFontSize?: number;
  className?: string;
  customPillText?: string;
};

const StyledContainer = styled('div', {
  shouldForwardProp: (prop) => !['rewardCampaignType', 'clickable'].includes(prop.toString()),
})<{ rewardCampaignType: RewardPillWithTextProps['rewardCampaignType']; clickable: boolean }>(
  ({ clickable, theme }) => ({
    display: 'flex',
    alignItems: 'center',
    overflow: 'hidden',
    columnGap: 7,
    '*': {
      mixBlendMode: 'unset !important',
    },
    '.reward-pill': {
      textWrap: 'nowrap',
      borderRadius: 25,
      fontSize: 12,
      fontWeight: 600,
      letterSpacing: 0,
      display: 'flex',
      alignItems: 'center',
      ':hover': {
        cursor: clickable ? 'pointer' : 'default',
      },
    },
    '.rewardPercent-text': {
      color: theme.vars.palette.text.primary,
      fontSize: 12,
      fontWeight: 600,
      letterSpacing: 0.24,
      whiteSpace: 'nowrap', // added for mobile view
      textOverflow: 'ellipsis',
      overflow: 'hidden',
    },
  }),
);

// default styles are from pdp buy-box
const getConfigByRewardCampaignType = (
  rewardCampaignType: CampaignType,
  theme: Theme,
): {
  iconImgProps: ImgHTMLAttributes<HTMLImageElement>;
  rewardTypeDisplay: string;
  containerStyle: CSSProperties;
} => {
  switch (rewardCampaignType) {
    case CampaignType.DailyDealsRewardCampaign:
      return {
        iconImgProps: {
          src: getImageAssetUrl('instant-reward-icon.webp'),
          alt: 'instant-reward-icon',
        },
        rewardTypeDisplay: 'instant',
        containerStyle: {
          color: (theme.vars as unknown as Theme).globalComponentPresets?.pill.dailyDeal.contrastText,
          backgroundColor: (theme.vars as unknown as Theme).globalComponentPresets?.pill.dailyDeal.main,
        },
      };
    case CampaignType.PurchaseRewardCampaign:
      return {
        iconImgProps: {
          src: getImageAssetUrl('purchase-reward-icon.webp'),
          alt: 'purchase-reward-icon',
        },
        rewardTypeDisplay: 'purchase',
        containerStyle: {
          color: (theme.vars as unknown as Theme).globalComponentPresets?.pill.purchase.contrastText,
          backgroundColor: (theme.vars as unknown as Theme).globalComponentPresets?.pill.purchase.main,
        },
      };
    case CampaignType.AffiliateRewardCampaign:
      return {
        iconImgProps: {
          src: getImageAssetUrl('affiliate-reward-icon.webp'),
          alt: 'affiliate-reward-icon',
        },
        rewardTypeDisplay: '',
        containerStyle: {
          color: (theme.vars as unknown as Theme).globalComponentPresets?.pill.affiliate.contrastText,
          backgroundColor: (theme.vars as unknown as Theme).globalComponentPresets?.pill.affiliate.main,
        },
      };
    case CampaignType.ContentRewardCampaign:
      return {
        iconImgProps: {
          src: getImageAssetUrl('content-reward-icon.webp'),
          alt: 'content-reward-icon',
        },
        rewardTypeDisplay: '',
        containerStyle: {
          color: (theme.vars as unknown as Theme).globalComponentPresets?.pill.content.contrastText,
          backgroundColor: (theme.vars as unknown as Theme).globalComponentPresets?.pill.content.main,
        },
      };
    case CampaignType.SurveyRewardCampaign:
      return {
        iconImgProps: {
          src: getImageAssetUrl('survey-reward-icon.webp'),
          alt: 'survey-reward-icon',
        },
        rewardTypeDisplay: '',
        containerStyle: {
          color: (theme.vars as unknown as Theme).globalComponentPresets?.pill.survey.contrastText,
          backgroundColor: (theme.vars as unknown as Theme).globalComponentPresets?.pill.survey.main,
        },
      };
    default:
      return {
        iconImgProps: {
          src: undefined,
          alt: 'affiliate-reward-icon',
        },
        rewardTypeDisplay: '',
        containerStyle: {
          color: (theme.vars as unknown as Theme).globalComponentPresets?.pill.default.contrastText,
          backgroundColor: (theme.vars as unknown as Theme).globalComponentPresets?.pill.default.main,
        },
      };
  }
};

export const RewardPillWithText = ({
  rewardAmount,
  rewardCampaignType,
  rewardPercent,
  rewardPercentText,
  rewardPercentTextOverride,
  containerStyle: containerStyleOverride = {},
  pillContainerStyle = {},
  baseFontSize = 14,
  onPillClick,
  className,
  customPillText,
}: RewardPillWithTextProps) => {
  const theme = useTheme();

  const { iconImgProps, rewardTypeDisplay, containerStyle } = getConfigByRewardCampaignType(rewardCampaignType, theme);

  const { ...restIconImgProps } = iconImgProps;

  const pillDefaultPadding = {
    paddingTop: `${baseFontSize / 3}px`, // top slightly larger than bottom to adjust text alignment
    paddingLeft: `${baseFontSize * 0.95}px`,
    paddingBottom: `${baseFontSize / 3}px`,
    paddingRight: `${baseFontSize * 0.95}px`,
  };

  const subfixText = rewardPercentTextOverride
    ? rewardPercentTextOverride
    : rewardPercent
    ? Math.round(rewardPercent) + '% ' + (rewardPercentText ?? `${rewardTypeDisplay} reward`)
    : null;

  const pillText = customPillText ?? formatMoney(rewardAmount);

  return (
    <StyledContainer
      rewardCampaignType={rewardCampaignType}
      style={{
        visibility: rewardAmount || customPillText ? 'visible' : 'hidden',
        display: 'flex',
        ...containerStyleOverride,
      }}
      clickable={!!onPillClick}
      className={className}
    >
      <Box sx={{ borderRadius: '999px', backgroundColor: '#fff', display: 'flex' }}>
        <Box
          className="reward-pill"
          sx={{
            ...containerStyle,
            ...pillDefaultPadding,
            ...pillContainerStyle,
            fontWeight: 600,

            fontFamily: 'inherit',
          }}
          onClick={onPillClick}
        >
          {iconImgProps.src && !customPillText ? (
            <img
              {...restIconImgProps}
              style={{
                maxWidth: baseFontSize,
                maxHeight: baseFontSize,
                objectFit: 'contain',
                marginRight: baseFontSize * 0.416,
                verticalAlign: 'middle',
              }}
            />
          ) : null}
          <Box
            component="span"
            sx={{
              lineHeight: '1.3em',
              margin: 0,
              padding: 0,
            }}
          >
            {pillText}
          </Box>
        </Box>
      </Box>

      {subfixText ? (
        <div
          className="rewardPercent-text"
          style={{
            fontWeight: 600,
            fontFamily: 'inherit',
          }}
        >
          {subfixText}
        </div>
      ) : null}
    </StyledContainer>
  );
};
